import { FramesManager } from '../../../view/frames-manager'
import { AddressLike, DistanceUnit, GeolocationCoords } from './geolocation-distance'

/**
 * Captures the user's current GPS position in the background and reports only
 * what the user can act on.
 *
 * Deliberately NOT an `EditableFieldProps` field: there is no value for a user
 * to enter, so prefilled / read-only / disabled / label / tooltip / inline help
 * have no meaning here. Extending the editable base would advertise a dozen
 * props the component silently ignores. It is still a *bound* component — it
 * writes the captured position through `onChange` — and it still participates
 * in required validation and error focus; it just does so without a field
 * shell. See `.claude/docs/ui2-geolocation-field.md`.
 */
export const UI2_GEOLOCATION_FIELD_DEFAULT_PROPS: Partial<UI2GeolocationFieldProps> = {
  watchPosition: true,
  /**
   * Mobile default is 1.5 s; web default is 5 s. Set this explicitly when
   * the update cadence matters for your flow.
   */
  minSecondsBetweenUpdates: 1.5,
  distanceUnit: 'mi',
}

export interface UI2GeolocationFieldProps {
  /**
   * Bound position. Optional — the component captures and reports location
   * whether or not a schema path is bound to it. Without a binding it is
   * purely informational and cannot block submit.
   */
  value?: GeolocationCoords
  onChange?: (value: GeolocationCoords) => void
  /**
   * When bound, an unavailable location blocks submit and the unavailable
   * banner switches from `warning` to `danger`. Without a bound `value` there
   * is no path to key an error to, so this only picks the banner tone.
   */
  isRequired?: boolean

  /** Continuously listen for GPS updates instead of a one-shot capture. */
  watchPosition?: boolean
  /**
   * Minimum time in seconds between position updates in watch mode.
   * Ignored when `watchPosition` is false.
   */
  minSecondsBetweenUpdates?: number

  /**
   * Where the user is expected to be. Accepts an `address` object (the
   * `entity.json#/definitions/address` shape, distance read off its
   * `geolocation`) or a bare `{latitude, longitude}`. When it resolves and a
   * position is captured, the component shows the distance between the two
   * plus an Open in Maps action.
   */
  destination?: AddressLike
  /** Units for the destination distance. Defaults to miles. */
  distanceUnit?: DistanceUnit
  /** Wording for the Open in Maps action. */
  openInMapsText?: string

  /**
   * Show an informational banner once a position is captured. Off by default —
   * a working location is not news, and a banner per field is noise in a long
   * form.
   */
  showFoundBanner?: boolean
  /** Body of the located banner. Only rendered when `showFoundBanner` is set. */
  foundBannerText?: string
  /** Body of the unavailable banner when the component is not required. */
  unavailableWarningText?: string
  /** Body of the unavailable banner when the component is required. */
  unavailableDangerText?: string
  /** Body of the banner shown when location permission has been denied. */
  permissionDeniedText?: string
  /**
   * Target of the permission banner's recovery action on web, where there is
   * no OS settings screen to open. Defaults to a web search for location
   * troubleshooting steps for the browser the user is actually in. Ignored on
   * native mobile, which opens device settings instead.
   */
  troubleshootingUrl?: string

  testID?: string
  accessibilityLabel?: string
  /**
   * Local translation table. Merged over `frames.getContext('translationTable')`
   * so component-level entries override the ambient table.
   */
  translationTable?: Record<string, string>
  frames?: FramesManager
}
